import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import { TextField, createStyles, Box } from '@material-ui/core';
import { makeStyles } from '../../themes';
import { CloseIcon, SearchIcon } from '../Icon';
import { PrimaryButton } from '../Button';
import { IconButton } from '../IconButton';
const SEARCH_BAR_TESTID_ROOT = 'components:search-bar';
export const SEARCH_BAR_TESTIDS = {
    form: `${SEARCH_BAR_TESTID_ROOT}:form`,
    input: `${SEARCH_BAR_TESTID_ROOT}:input`,
    submit: `${SEARCH_BAR_TESTID_ROOT}:submit`,
    close: `${SEARCH_BAR_TESTID_ROOT}:close`,
};
const useStyles = makeStyles(({ spacing, palette }) => createStyles({
    container: {
        display: 'flex',
    },
    wrapper: {
        width: '100%',
        '&$adornedStart': {
            paddingLeft: spacing(1),
        },
    },
    root: {
        '& $notchedOutline': {
            borderColor: 'transparent',
            borderWidth: 2,
        },
        '&:hover $notchedOutline': {
            borderColor: 'transparent',
            borderWidth: 2,
        },
        '&$focused $notchedOutline': {
            borderColor: 'transparent',
            borderWidth: 2,
        },
    },
    notchedOutline: {},
    focused: {},
    input: {
        height: 'auto',
        lineHeight: '1.25rem',
        textOverflow: 'ellipsis',
    },
    inputMarginDense: {
        padding: spacing(1, 1, 1, 0),
    },
    icon: {
        cursor: 'pointer',
        height: 24,
        padding: spacing(0.25),
        width: 'auto',
        color: palette.common.black,
    },
    adornedStart: {
        height: '100%',
        paddingLeft: spacing(1),
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },
    positionStart: {
        height: 'auto',
        maxHeight: 'none',
        padding: spacing(1, 0),
    },
    button: {
        borderTopLeftRadius: '0',
        borderBottomLeftRadius: '0',
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2,
        marginTop: -1,
        marginBottom: -1,
        borderLeft: `2px solid ${palette.action.disabled}`,
        minWidth: 'fit-content',
        '&:hover': {
            backgroundColor: palette.success.main,
            color: palette.secondary.contrastText,
            boxShadow: 'none',
            borderColor: 'transparent',
            borderLeftWidth: 2,
            borderLeftColor: palette.success.main,
        },
    },
    iconFocus: {
        color: palette.success.main,
    },
    focus: {
        backgroundColor: palette.success.main,
        color: palette.secondary.contrastText,
        boxShadow: 'none',
        borderColor: 'transparent',
        borderLeftWidth: 2,
        borderLeftColor: palette.success.main,
    },
    searchBar: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        border: `2px solid ${palette.action.disabled}`,
        borderRadius: 8,
    },
    searchBarHover: {
        border: `2px solid ${palette.success.main}`,
    },
    iconButton: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
}));
export const SearchBar = ({ value, onChange, onSubmit, onClear, showCloseIcon, submitButtonText = 'SEARCH', placeholder = 'Search...', }) => {
    const inputRef = useRef();
    const classes = useStyles();
    const [focus, setFocus] = useState(false);
    const [hover, setHover] = useState(false);
    const handleChange = (event) => {
        if (onChange) {
            onChange(event.target.value);
        }
    };
    const handleCloseIconClick = () => {
        var _a;
        if (onClear) {
            onClear();
        }
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const handleSubmit = (event) => {
        event.preventDefault();
        if (onSubmit) {
            onSubmit(inputRef.current.value);
        }
    };
    const handleFocus = () => {
        setFocus(true);
    };
    const handleBlur = () => {
        setFocus(false);
    };
    const handleHoverIn = () => {
        setHover(true);
    };
    const handleHoverOut = () => {
        setHover(false);
    };
    return (_jsx("form", Object.assign({ "data-testid": SEARCH_BAR_TESTIDS.form, className: classes.container, onSubmit: handleSubmit }, { children: _jsxs(Box, Object.assign({ className: `${classes.searchBar} ${focus || hover ? classes.searchBarHover : ''}`, onMouseEnter: handleHoverIn, onMouseLeave: handleHoverOut }, { children: [_jsx(TextField, { type: "search", variant: "outlined", size: "small", error: false, onChange: handleChange, className: classes.wrapper, inputRef: inputRef, placeholder: placeholder, value: value, onFocus: handleFocus, onBlur: handleBlur, InputProps: {
                        classes: {
                            root: classes.root,
                            notchedOutline: classes.notchedOutline,
                            focused: classes.focused,
                            input: classes.input,
                            inputMarginDense: classes.inputMarginDense,
                            adornedStart: classes.adornedStart,
                        },
                        inputProps: {
                            'data-testid': SEARCH_BAR_TESTIDS.input,
                        },
                        startAdornment: (_jsx(InputAdornment, Object.assign({ classes: { positionStart: classes.positionStart }, position: "start" }, { children: _jsx(SearchIcon, { className: `${classes.icon} ${focus || hover ? classes.iconFocus : ''}`, color: "secondary" }, void 0) }), void 0)),
                        // only show close icon if something has been typed
                        endAdornment: (showCloseIcon || !!value) && (_jsx(InputAdornment, Object.assign({ position: "end" }, { children: _jsx(IconButton, Object.assign({ "data-testid": SEARCH_BAR_TESTIDS.close, color: "default", classes: { root: classes.iconButton }, size: "small", onClick: handleCloseIconClick }, { children: _jsx(CloseIcon, { className: classes.icon }, void 0) }), void 0) }), void 0)),
                    } }, void 0),
                _jsx(PrimaryButton, Object.assign({ variant: 'text', color: "secondary", onClick: handleSubmit, className: `${classes.button} ${focus || hover ? classes.focus : ''}`, "data-testid": SEARCH_BAR_TESTIDS.submit }, { children: submitButtonText }), void 0)] }), void 0) }), void 0));
};
//# sourceMappingURL=SearchBar.js.map